import { Check, Shield, Sun, DoorOpen, Minus, Plus } from "lucide-react";
import { MeshPhoto } from "@/components/MeshPhoto";

export const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

export const preloadImages = (urls) => urls.filter(Boolean).forEach((u) => { const i = new Image(); i.src = u; });

const selCls = (on) =>
  on ? "border-[#207024] bg-green-50 shadow-md" : "border-zinc-200 bg-white hover:border-[#207024]/40";

export function StepModel({ models, modelKey, onPick }) {
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-bold text-zinc-900">Pick your door style</h1>
        <p className="text-zinc-600 mt-1 text-sm">Every price includes the door and professional installation.</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="swing-model-grid">
        {models.map((m) => (
          <button
            key={m.key}
            onClick={() => onPick(m.key)}
            data-testid={`swing-model-${m.key}`}
            className={`relative text-left rounded-2xl border-2 p-2.5 transition-[border-color,box-shadow,transform] duration-200 active:scale-[0.98] ${selCls(modelKey === m.key)}`}
          >
            {modelKey === m.key && (
              <span className="absolute top-2 right-2 h-6 w-6 rounded-full bg-[#207024] text-white flex items-center justify-center">
                <Check className="w-4 h-4" />
              </span>
            )}
            <div className="aspect-[1/2] bg-white rounded-lg overflow-hidden flex items-center justify-center">
              <img src={m.image_url} alt={m.name} loading="lazy" className="h-full w-full object-contain" />
            </div>
            <div className="mt-2 font-bold text-zinc-900 leading-tight">{m.name}</div>
            <div className="text-[11px] text-zinc-500 leading-snug min-h-[2rem]">{m.description}</div>
            <div className="mt-1.5 inline-block rounded-md bg-[#207024] text-white text-sm font-bold px-2 py-0.5" data-testid={`swing-model-price-${m.key}`}>
              {fmt(m.price)}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function StepColor({ colors, color, setColor }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-zinc-900">Choose your frame color</h2>
        <p className="text-zinc-600 text-sm mt-1">All colors are the same price.</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {colors.map((c) => (
          <button
            key={c.id}
            onClick={() => setColor(c.id)}
            data-testid={`swing-color-${c.id}`}
            className={`rounded-2xl border-2 p-4 flex flex-col items-center gap-2 transition-[border-color,box-shadow] ${selCls(color === c.id)}`}
          >
            <div className="h-14 w-14 rounded-full border-2 border-zinc-300" style={{ backgroundColor: c.hex }} />
            <div className="text-sm font-semibold text-zinc-900">{c.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

const UPGRADE_ICONS = { swing_upgrade_petscreen: Shield, swing_upgrade_solar: Sun };

export function StepUpgrade({ upgrades, upgradeKey, setUpgradeKey }) {
  const options = [{ key: null, name: "Standard Screen", price: 0, description: "Reliable mesh included with every door." }, ...upgrades];
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-zinc-900">Upgrade your screen?</h2>
        <p className="text-zinc-600 text-sm mt-1">Stronger. Cooler. Longer lasting.</p>
      </div>
      <div className="grid gap-3">
        {options.map((u) => {
          const Icon = UPGRADE_ICONS[u.key] || DoorOpen;
          const on = upgradeKey === u.key;
          const meshKind = { swing_upgrade_petscreen: "petscreen", swing_upgrade_solar: "solar" }[u.key];
          return (
            <div key={u.key || "standard"}>
            <button
              onClick={() => setUpgradeKey(u.key)}
              data-testid={`swing-upgrade-${u.key || "standard"}`}
              className={`w-full text-left rounded-2xl border-2 overflow-hidden transition-[border-color,box-shadow] ${selCls(on)}`}
            >
              {u.image_url && (
                <div className="w-full h-24 bg-zinc-100">
                  <img src={u.image_url} alt={u.name} width={1267} height={351} loading="eager" decoding="async"
                    onError={(e) => { if (!e.currentTarget.dataset.fb && meshKind) { e.currentTarget.dataset.fb = "1"; e.currentTarget.src = `/mesh/${meshKind === "petscreen" ? "pet" : "solar"}-full.webp`; } }}
                    className="w-full h-24 object-cover" data-testid={`swing-upgrade-img-${u.key}`} />
                </div>
              )}
              <div className="p-4 flex items-start gap-3">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${on ? "bg-[#207024] text-white" : "bg-zinc-100 text-zinc-600"}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-bold text-zinc-900">{u.name.replace(" (Swinging Door)", "")}</h3>
                    <span className="text-sm font-semibold text-[#207024] shrink-0">{u.price ? `+${fmt(u.price)}/door` : "Included"}</span>
                  </div>
                  <p className="text-sm text-zinc-600 mt-0.5">{u.description}</p>
                </div>
              </div>
            </button>
            {meshKind && <div className="px-2"><MeshPhoto kind={meshKind} /></div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function StepQty({ qty, setQty }) {
  const btn = "h-14 w-14 rounded-full border-2 border-zinc-200 bg-white flex items-center justify-center active:scale-95 transition-transform";
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-zinc-900">How many doors?</h2>
      <div className="flex items-center justify-center gap-6">
        <button className={btn} onClick={() => setQty(Math.max(1, qty - 1))} data-testid="swing-qty-minus"><Minus className="w-5 h-5" /></button>
        <div className="text-5xl font-bold text-[#207024] w-16 text-center" data-testid="swing-qty-value">{qty}</div>
        <button className={btn} onClick={() => setQty(Math.min(20, qty + 1))} data-testid="swing-qty-plus"><Plus className="w-5 h-5" /></button>
      </div>
      <p className="text-center text-sm text-zinc-500">We measure every opening in person, so no need to know sizes.</p>
    </div>
  );
}
